'use client';

/**
 * AuthGuard - Client-Side Authentication Gate
 *
 * Features:
 * - Wraps content that requires authentication
 * - Shows loading state while checking auth
 * - Redirects to login if not authenticated
 * - Supports different auth requirements (required, staff, admin, citizen)
 * - Shows access denied for role mismatches
 */

import React, { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Loader2, Lock } from 'lucide-react';
import { useAppShell, useIsAuthenticated, useIsStaff, useIsAdmin, useIsCitizen } from '../AppShellContext';
import type { AuthRequirement } from '@/lib/routes/types';
import { AUTH_REDIRECTS, ROLE_DEFAULT_ROUTES } from '@/lib/routes/config';
import { isTransientAccessFailure } from '../accessReadRecovery';
import { collectHeldRoles, formatHeldRoles, ROLES_UNAVAILABLE_LABEL } from '@/lib/auth/held-roles';
import { SignOutForm } from '@/components/auth/SignOutForm';

// ============================================================================
// Types
// ============================================================================

interface AuthGuardProps {
  /** Authentication requirement level */
  requirement: AuthRequirement;

  /** Content to render when authorized */
  children: React.ReactNode;

  /** Custom loading component */
  loadingComponent?: React.ReactNode;

  /** Custom access denied component */
  accessDeniedComponent?: React.ReactNode;

  /** Redirect URL when not authenticated (overrides default) */
  redirectTo?: string;
}

// ============================================================================
// Component
// ============================================================================

export function AuthGuard({
  requirement,
  children,
  loadingComponent,
  accessDeniedComponent,
  redirectTo,
}: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isAuthLoading: isLoading, accessControlState, refetchAccessControl } = useAppShell();
  const isAuthenticated = useIsAuthenticated();
  const isStaff = useIsStaff();
  const isAdmin = useIsAdmin();
  const isCitizen = useIsCitizen();

  // Wait for BOTH auth AND access control state to finish loading.
  // isAuthLoading tracks Supabase session resolution.
  // accessControlState.loading tracks the /api/access-control/context fetch.
  // Without waiting for both, role checks (isStaff/isAdmin) see null userContext
  // and incorrectly deny access.
  const isAccessControlLoading = accessControlState?.loading ?? true;

  const [checkComplete, setCheckComplete] = useState(false);
  const [accessStatus, setAccessStatus] = useState<'loading' | 'granted' | 'denied' | 'redirect'>('loading');

  useEffect(() => {
    if (isLoading || isAccessControlLoading) {
      return;
    }

    // Check access based on requirement
    const checkAccess = () => {
      switch (requirement) {
        case 'none':
          return 'granted';

        case 'optional':
          return 'granted';

        case 'required':
          if (!isAuthenticated) {
            return 'redirect';
          }
          return 'granted';

        case 'staff':
          if (!isAuthenticated) {
            return 'redirect';
          }
          if (!isStaff) {
            return 'denied';
          }
          return 'granted';

        case 'admin':
          if (!isAuthenticated) {
            return 'redirect';
          }
          if (!isAdmin) {
            return 'denied';
          }
          return 'granted';

        case 'platform':
          if (!isAuthenticated) {
            return 'redirect';
          }
          if (accessControlState?.userContext?.is_platform_admin !== true) {
            return 'denied';
          }
          return 'granted';

        case 'citizen':
          if (!isAuthenticated) {
            return 'redirect';
          }
          if (!isCitizen) {
            return 'denied';
          }
          return 'granted';

        default:
          return 'denied';
      }
    };

    const status = checkAccess();
    setAccessStatus(status);
    setCheckComplete(true);

    if (status === 'redirect') {
      // Determine redirect URL
      const targetUrl = redirectTo || AUTH_REDIRECTS.unauthenticated;
      const returnUrl = encodeURIComponent(pathname);
      router.push(`${targetUrl}?returnTo=${returnUrl}`);
    }
  }, [isLoading, isAccessControlLoading, isAuthenticated, isStaff, isAdmin, isCitizen, requirement, user, accessControlState?.userContext?.is_platform_admin, pathname, router, redirectTo]);

  // A transport failure is unknown access, never a resolved role denial. Keep protected
  // content hidden in this render, including when the previous check was granted.
  // A transient failure is re-read by AppShellProvider on its own (CAUSE-B F2): say so, keep Retry, and the
  // content replaces this view as soon as a read succeeds. A 401/403 is final: nothing is retrying.
  if (!isLoading && !isAccessControlLoading && ROLE_BASED.has(requirement) && accessControlState?.error) {
    const reconnecting = isTransientAccessFailure(accessControlState.error);
    return (
      <div role="alert" className="flex flex-col items-center justify-center min-h-[400px] gap-4 px-4">
        <p>Couldn't load your access. Check your connection and try again.</p>
        {reconnecting && (
          <p role="status" className="flex items-center gap-2 text-sm text-ink-muted">
            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" aria-hidden="true" />
            Reconnecting automatically...
          </p>
        )}
        <AccountExplanation email={user?.email} roles={ROLES_UNAVAILABLE_LABEL} requirement={requirement} />
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button type="button" onClick={() => void refetchAccessControl()}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-ink">
            Retry
          </button>
          <SignOutButton />
        </div>
      </div>
    );
  }

  // Revalidation must hide a cached denial or grant while its role read is pending.
  if (isLoading || isAccessControlLoading || !checkComplete || accessStatus === 'loading') {
    return loadingComponent || <AuthLoadingState />;
  }

  // Handle redirect (show loading while redirecting)
  if (accessStatus === 'redirect') {
    return loadingComponent || <AuthLoadingState message="Redirecting to login..." />;
  }

  // Handle access denied
  if (accessStatus === 'denied') {
    return accessDeniedComponent || <AccessDeniedState requirement={requirement} />;
  }

  // Access granted
  return <>{children}</>;
}

const ROLE_BASED: ReadonlySet<AuthRequirement> = new Set<AuthRequirement>(['staff', 'admin', 'platform', 'citizen']);

// ============================================================================
// Loading State
// ============================================================================

interface AuthLoadingStateProps {
  message?: string;
}

function AuthLoadingState({ message = 'Checking authentication...' }: AuthLoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      <p className="text-sm text-ink-muted">{message}</p>
    </div>
  );
}

// ============================================================================
// Account explanation (shared by the unknown-access and access-denied states)
// ============================================================================

/** The role each requirement needs, in the words of the role names users see. */
const REQUIRED_ROLE: Partial<Record<AuthRequirement, string>> = {
  staff: 'Officer, Supervisor or Administrator',
  admin: 'Administrator',
  platform: 'Platform Admin',
  citizen: 'Citizen portal user',
};

interface AccountExplanationProps {
  email: string | null | undefined;
  /** Held role names from the access-control context, or ROLES_UNAVAILABLE_LABEL when that context is unknown. */
  roles: string;
  requirement: AuthRequirement;
}

function AccountExplanation({ email, roles, requirement }: AccountExplanationProps) {
  return (
    <div className="text-center text-sm text-ink-secondary max-w-md space-y-1 break-words">
      <p>Signed in as: {email || 'Email unavailable'}</p>
      <p>Your role: {roles}</p>
      <p>Required role: {REQUIRED_ROLE[requirement] ?? 'A role with access to this page'}</p>
    </div>
  );
}

function SignOutButton() {
  return (
    <SignOutForm>
      <button
        type="submit"
        className="px-4 py-2 text-sm text-ink bg-raised hover:bg-floating rounded-lg transition-colors cursor-pointer"
      >
        Sign out
      </button>
    </SignOutForm>
  );
}

// ============================================================================
// Access Denied State
// ============================================================================

interface AccessDeniedStateProps {
  requirement: AuthRequirement;
}

function AccessDeniedState({ requirement }: AccessDeniedStateProps) {
  const router = useRouter();
  const { user, accessControlState, refetchAccessControl } = useAppShell();
  // The roles the app actually checked (DB-backed), never the JWT role hint; unknown is not "none".
  const userContext = accessControlState?.userContext;
  const heldRoles = userContext ? formatHeldRoles(collectHeldRoles(userContext)) : ROLES_UNAVAILABLE_LABEL;

  // Determine appropriate redirect based on user's role
  const getHomeRoute = () => {
    const role = user?.role;
    if (role && ROLE_DEFAULT_ROUTES[role as keyof typeof ROLE_DEFAULT_ROUTES]) {
      return ROLE_DEFAULT_ROUTES[role as keyof typeof ROLE_DEFAULT_ROUTES];
    }
    return '/dashboard';
  };

  const getMessage = () => {
    switch (requirement) {
      case 'staff':
        return 'This page is only accessible to officers, supervisors, and administrators.';
      case 'admin':
        return 'This page is only accessible to administrators.';
      case 'platform':
        return 'This page is only accessible to platform administrators.';
      case 'citizen':
        return 'This page is only accessible to citizen portal users.';
      default:
        return 'You do not have permission to access this page.';
    }
  };

  return (
    <div className="relative z-10 flex flex-col items-center justify-center min-h-[400px] gap-6 px-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20">
        <Lock className="w-8 h-8 text-blue-400" />
      </div>

      <div className="text-center max-w-md">
        <h2 className="text-xl font-semibold text-ink mb-2">Access Denied</h2>
        <p className="text-sm text-ink-muted">{getMessage()}</p>
      </div>

      <AccountExplanation email={user?.email} roles={heldRoles} requirement={requirement} />
      <p className="text-center text-sm text-ink-muted max-w-md">
        To get access, ask your agency administrator to give this account the required role, then select Retry.
        If you meant to use a different account, sign out and sign back in.
      </p>

      <div className="relative z-20 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => void refetchAccessControl()}
          className="px-4 py-2 text-sm text-ink bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors cursor-pointer"
        >
          Retry
        </button>
        <SignOutButton />
        <button
          type="button"
          onClick={() => router.back()}
          className="px-4 py-2 text-sm text-ink-secondary hover:text-ink bg-raised hover:bg-floating rounded-lg transition-colors cursor-pointer"
        >
          Go Back
        </button>
        <button
          type="button"
          onClick={() => router.push(getHomeRoute())}
          className="px-4 py-2 text-sm text-ink-secondary hover:text-ink bg-raised hover:bg-floating rounded-lg transition-colors cursor-pointer"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default AuthGuard;
